import React from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import ButtonHapticWrapper from "../Button/ButtonHapticWrapper";
import LevelBadge from "../Ui/LevelBadge";
import Avatar from "../Ui/Avatar";
import Tooltip from "../Tooltip";
import UserPanel from "../UserPanel";
import { useShallow } from "zustand/react/shallow";
import useStorePlayer from "store/useStorePlayer";
import useStoreUserPanel from "store/useStoreUserPanel";
import HostConfig from "integrations/host/HostConfig";


type HeaderProps = {
    children?: React.ReactNode;
};

const Header = ({ children }: HeaderProps) => {
    const { t } = useTranslation();
    const { avatar, username, level, hasSavedAccount } = useStorePlayer(useShallow((state) => ({
        avatar: state.player?.avatar ?? "",
        username: state.player?.username ?? "",
        level: state.player?.level ?? 1,
        hasSavedAccount: state.player?.hasSavedAccount ?? false,
    })));
    const hasAnimated = React.useRef(!!username);
    const [showWelcome, setShowWelcome] = React.useState(!hasAnimated.current);
    const dismissWelcome = React.useCallback(() => setShowWelcome(false), []);
    const { isOpen, open, close } = useStoreUserPanel();
    const isCrazyGamesAccount = HostConfig.host === "crazygames"
        && hasSavedAccount;
    const wasCrazyGamesAccount = React.useRef(isCrazyGamesAccount);

    React.useEffect(() => {
        if(isCrazyGamesAccount && !wasCrazyGamesAccount.current) {
            setShowWelcome(true);
        }

        wasCrazyGamesAccount.current = isCrazyGamesAccount;
    }, [isCrazyGamesAccount]);

    const handleAvatarClick = () => {
        if(isCrazyGamesAccount) {
            return;
        }

        open();
    };

    return (
        <header className="h-16 md:h-20 mt-1 flex items-center justify-between relative z-10">
            <div className="flex items-center gap-2">
                {children}
            </div>

            <AnimatePresence>
                {username && (
                    <motion.div
                        initial={hasAnimated.current ? false : { opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        onAnimationComplete={() => { hasAnimated.current = true; }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        className="flex items-center gap-2"
                    >
                        <Tooltip
                            text={t(
                                isCrazyGamesAccount
                                    ? "header.crazygames_account"
                                    : "header.welcome",
                                { username },
                            )}
                            visible={showWelcome && !!username}
                            placement="bottom"
                            duration={3000}
                            onDismiss={dismissWelcome}
                        >
                            <ButtonHapticWrapper disabled={isCrazyGamesAccount}>
                                <div
                                    className={`relative ${isCrazyGamesAccount ? "cursor-default" : "cursor-pointer"}`}
                                    onClick={handleAvatarClick}
                                    title={isCrazyGamesAccount ? t("header.crazygames_account") : undefined}
                                    aria-disabled={isCrazyGamesAccount}
                                >
                                    <Avatar src={avatar} className="w-12 h-12 md:w-14 md:h-14" />
                                    {hasSavedAccount === false && (
                                        <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                                            <svg className="w-5 h-5 md:w-6 md:h-6 animate-shake" fill="#fbbf24" viewBox="0 0 32 32">
                                                <path d="M10.656 8.864q0-2.208 1.568-3.776t3.776-1.568 3.776 1.568 1.6 3.776q0 0.256-0.064 0.448l-1.76 6.944q-0.096 1.408-1.12 2.368t-2.432 0.96q-1.376 0-2.4-0.928t-1.152-2.304q-0.32-0.96-0.672-2.112t-0.736-2.784-0.384-2.592zM12.416 24.928q0-1.472 1.056-2.496t2.528-1.056 2.528 1.056 1.056 2.496q0 1.504-1.056 2.528t-2.528 1.056-2.528-1.056-1.056-2.528z" />
                                            </svg>
                                        </div>
                                    )}
                                    <LevelBadge level={level} className="-bottom-0.5 -right-0.5 w-5 h-5 md:w-6 md:h-6 text-[9px] md:text-[11px]" />
                                </div>
                            </ButtonHapticWrapper>
                        </Tooltip>
                        {!isCrazyGamesAccount && (
                            <UserPanel isOpen={isOpen} onClose={close} />
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
